import { fmtEUR, vatLabel } from "@/lib/api";
import { printProforma } from "@/lib/proforma";
import { printKitchenTicket } from "@/lib/kitchenTicket";

const COMPANION_URL = "http://127.0.0.1:9100";

/**
 * Print a non-fiscal order. Tries the local Companion Print Service first
 * (silent direct-to-printer). If unreachable, falls back to the browser
 * print dialog via a hidden iframe.
 *
 * @param {object} opts
 * @param {string[]} opts.businessLines      6 header lines (επιχείρηση)
 * @param {boolean[]} opts.businessLinesBold per-line bold flags
 * @param {Array}    opts.items
 * @param {number}   opts.total
 * @param {string}   opts.printerName        Windows printer name
 * @param {"kitchen"|"a4"} opts.printerType  paper format
 * @param {number|null} opts.globalOrderNo   «Αρ. Παραγγελίας» — rendered
 *                                           in the header next to the
 *                                           date so the slip can be
 *                                           matched against the digital
 *                                           backlog (kitchen-format
 *                                           consistency with the
 *                                           restaurant module).
 */
export async function printOrder({
  businessLines = [],
  businessLinesBold = [],
  items = [],
  total = 0,
  printerName = "",
  printerType = "kitchen",
  globalOrderNo = null,
  // TICKETA context (kitchen path only)
  module = "RETAIL",
  tableName = null,
  orderNo = null,
  operatorName = null,
  // Edit-correction support — when true, the kitchen ticket renders
  // a «ΔΙΟΡΘΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ» banner and any item with `is_void:true`
  // is printed with the «ΑΚΥΡΩΘΗΚΕ» prefix instead of the normal row.
  correction = false,
}) {
  const isKitchen = printerType !== "a4";
  // 1) Try Companion service — silent printing to the configured Windows printer
  const text = buildPlainText({ businessLines, businessLinesBold, items, total, isKitchen, globalOrderNo });
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 800);
    // FText Print Engine v3.x → /print-ticket with {route|profile, text}.
    // The route id sits in `printerName` (operator picks one of the v3
    // configured routes: kitchen / bar / cashier / tameio). The legacy
    // `printer` field is kept so a v2 simple agent still works (it ignores
    // route/profile, v3 ignores printer).
    const id = (printerName || "").trim();
    const res = await fetch(`${COMPANION_URL}/print-ticket`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ route: id, profile: id, printer: id, text }),
      signal: ctrl.signal,
    });
    clearTimeout(tid);
    if (res.ok) {
      const j = await res.json();
      if (j.ok) return { ok: true, mode: "companion", printer: j.printer || j.route || id };
    }
  } catch {
    // Companion not available — fall through to browser
  }

  // 2) Fallback — open a popup. Kitchen tickets use the unified TICKETA
  // template (`lib/kitchenTicket.js`); receipt-like A4 prints use the
  // proforma template (`lib/proforma.js`). Browser print dialog is
  // triggered in both branches.
  if (isKitchen) {
    printKitchenTicket({
      spot: printerName || "ΕΚΤΥΠΩΤΗΣ",
      module,
      tableName,
      orderNo: orderNo ?? globalOrderNo,
      operatorName,
      correction,
      items: items.map((it) => ({
        qty: Number(it.qty || 1),
        description: it.description,
        comments: it.comments || [],
        free_text: it.free_text || null,
        is_void: !!it.is_void,
      })),
    });
    return { ok: true, mode: "browser" };
  }
  await printProforma({
    settings: { business_lines: businessLines, business_lines_bold: businessLinesBold },
    globalOrderNo,
    items: items.map((it) => ({
      description: it.description,
      qty: Number(it.qty || 1),
      price: Number(it.price || 0),
      vat_rate: it.vat_rate,
      comments: it.comments || [],
      free_text: it.free_text || null,
    })),
    total,
  });
  return { ok: true, mode: "browser" };
}

/** Build raw plain-text receipt for thermal printer (Companion mode).
 *  Kitchen → 80 chars / line (80mm wide thermal in Font A).
 *  Α4      → 80 chars / line (μένει συμβατό με ESC/POS · ο driver wraps to A4).
 */
function buildPlainText({ businessLines, businessLinesBold, items, total, isKitchen, globalOrderNo }) {
  // 80mm θερμικός εκτυπωτής τυπώνει 76 χαρακτήρες ανά γραμμή σε Font A —
  // αν περάσουμε 80 κόβονται οι τελευταίοι 3 από τη φυσική εκτύπωση.
  const W = isKitchen ? 76 : 80;
  const out = [];
  const center = (s) => {
    const t = String(s);
    if (t.length >= W) return t.slice(0, W);
    const pad = Math.floor((W - t.length) / 2);
    return " ".repeat(pad) + t;
  };
  const sep = () => "-".repeat(W);
  const between = (l, r) => {
    const pad = Math.max(1, W - l.length - r.length);
    return l + " ".repeat(pad) + r;
  };

  // v3 RestaurantFormatter applies bold automatically based on content
  // patterns (title, ΤΡΑΠΕΖΙ/ΓΥΡΟΣ/ΤΜΗΜΑ, qty-X product rows). We emit
  // clean ASCII without ESC bytes so the local engine can re-encode safely
  // through the configured codepage (cp1253) and add its own cut command.

  const lines = (businessLines || []).map((s) => (s || "").trim());
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    if (!ln) continue;
    out.push(center(ln));
  }
  out.push(sep());
  // New header: «ΑΡ.ΠΑΡΑΓ: X» στην αριστερή στήλη, ημερομηνία/ώρα δεξιά —
  // ίδιο layout με την εκτύπωση παρασκευής ειδών του Restaurant.
  const dt = formatDateTime();
  const leftHdr = globalOrderNo != null ? `ΑΡ.ΠΑΡΑΓ: ${globalOrderNo}` : "";
  out.push(between(leftHdr, dt));
  out.push(sep());

  for (const it of items) {
    const gross = it.qty * it.price;
    const disc = it.discount_percent ? (gross * it.discount_percent) / 100 : 0;
    const net = Math.max(0, gross - disc);
    const desc = `${it.description} (${vatLabel(it.vat_rate)})`;
    out.push(desc);
    // Match the v3 RestaurantFormatter «\d+\s*[xX×]\s+» trigger so this
    // qty line lands as a bold product row on the receipt.
    const left = `  ${it.qty} X ${Number(it.price).toFixed(2)}${
      it.discount_percent ? `  (-${it.discount_percent}%)` : ""
    }`;
    out.push(between(left, fmtNum(net)));
  }
  out.push(sep());
  out.push(between("ΣΥΝΟΛΟ", fmtNum(total)));
  out.push(sep());
  out.push(center("ΣΑΣ ΕΥΧΑΡΙΣΤΟΥΜΕ"));
  out.push(sep());
  return out.join("\n") + "\n" + (isKitchen ? "\n\n\n" : ""); // feed lines for cutter on thermal
}

function fmtNum(n) {
  return (Number(n) || 0)
    .toFixed(2)
    .replace(".", ",")
    .concat(" EUR");
}

function formatDateTime() {
  const d = new Date();
  return (
    String(d.getDate()).padStart(2, "0") +
    "/" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "/" +
    d.getFullYear() +
    " " +
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0")
  );
}

/* --- Browser fallback (iframe) --- */
function browserPrint(html) {
  let frame = document.getElementById("ftext-print-frame");
  if (frame) frame.remove();
  frame = document.createElement("iframe");
  frame.id = "ftext-print-frame";
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);
  frame.onload = () => {
    setTimeout(() => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (e) {
        console.error("Browser print failed:", e);
      }
    }, 150);
  };
  const doc = frame.contentDocument || frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
}

function buildHtml({ businessLines, businessLinesBold, items, total, isKitchen, globalOrderNo }) {
  const dt = formatDateTime();
  const rows = items
    .map((it) => {
      const gross = it.qty * it.price;
      const disc = it.discount_percent ? (gross * it.discount_percent) / 100 : 0;
      const net = Math.max(0, gross - disc);
      const lineTwo = `   ${it.qty} x ${Number(it.price).toFixed(2)}${
        it.discount_percent ? `  (-${it.discount_percent}%)` : ""
      }`;
      return `
        <div class="row">
          <div class="row-desc">${escapeHtml(it.description)} <span class="vat">(${escapeHtml(
        vatLabel(it.vat_rate)
      )})</span></div>
          <div class="row-line"><span>${escapeHtml(lineTwo)}</span><span>${fmtEUR(net)}</span></div>
        </div>`;
    })
    .join("");

  const boldArr = businessLinesBold || [];
  const businessHtml = (businessLines || [])
    .map((s, i) => {
      const txt = (s || "").trim();
      if (!txt) return "";
      const cls = boldArr[i] ? "biz biz-bold" : "biz";
      return `<div class="${cls}">${escapeHtml(txt)}</div>`;
    })
    .join("");

  // Kitchen → 80mm thermal layout · A4 → standard A4 with wider columns
  const pageCss = isKitchen
    ? `@page { size: 80mm auto; margin: 4mm; } body { width: 76mm; font-size:12px; }`
    : `@page { size: A4; margin: 18mm; } body { width: auto; font-size:13px; }`;

  // Header line: «ΑΡ.ΠΑΡΑΓ: X» left, datetime right — same layout as the
  // restaurant kitchen ticket so paper from both modules looks identical.
  const orderHeader = globalOrderNo != null
    ? `<div class="order-hdr"><span class="order-no">ΑΡ.ΠΑΡΑΓ: ${escapeHtml(String(globalOrderNo))}</span><span class="order-dt">${escapeHtml(dt)}</span></div>`
    : `<div class="order-hdr"><span></span><span class="order-dt">${escapeHtml(dt)}</span></div>`;

  return `<!doctype html><html lang="el"><head><meta charset="utf-8"><title>Παραγγελία</title>
<style>
${pageCss}
body { font-family: Arial, "Helvetica Neue", Helvetica, sans-serif; color:#000; margin:0; padding:6px; }
.biz { text-align:center; }
.biz-bold { font-weight:900; }
.sep { border-top:1px dashed #000; margin:6px 0; }
.order-hdr { display:flex; justify-content:space-between; font-weight:900; font-size:${isKitchen ? "14px" : "16px"}; margin:6px 0; font-variant-numeric:tabular-nums; }
.order-no { letter-spacing:1px; }
.order-dt { letter-spacing:1px; }
.row { margin-bottom:4px; }
.row-desc { font-weight:bold; }
.vat { font-weight:normal; font-size:10px; }
.row-line { display:flex; justify-content:space-between; font-variant-numeric:tabular-nums; }
.total-row { display:flex; justify-content:space-between; font-size:${isKitchen ? "16px" : "20px"}; font-weight:900; margin:6px 0; }
.thanks { text-align:center; font-weight:bold; margin:6px 0; letter-spacing:1px; }
</style></head><body>
${businessHtml || '<div class="biz" style="font-style:italic">(χωρίς στοιχεία επιχείρησης)</div>'}
<div class="sep"></div>
${orderHeader}
<div class="sep"></div>
${rows || '<div style="text-align:center">(κενή παραγγελία)</div>'}
<div class="sep"></div>
<div class="total-row"><span>ΣΥΝΟΛΟ</span><span>${fmtEUR(total)}</span></div>
<div class="sep"></div>
<div class="thanks">ΣΑΣ ΕΥΧΑΡΙΣΤΟΥΜΕ</div>
</body></html>`;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
